import { Icon } from "@iconify/react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Container,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppButton from "../../components/app-button/AppButton";
import Iconify from "../../components/iconify/Iconify";
import { getAttributesKeyValue } from "../../reduxStore/slices/attributeSlice";
import { getCategoriesKeyValue } from "../../reduxStore/slices/categorySlice";
import {
  addItem,
  getItemDetails,
  getItemTypes,
  getUnits,
  updateItem,
} from "../../reduxStore/slices/itemSlice";
import { useNotify } from "../../utils/snackbarUtils";
import ItemAdditionalInfo from "./ItemAdditionalInfo";
import ItemImagesView from "./ItemImagesView";

export default function Item() {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const storeId = user?.storeId;
  const { uid } = useParams();

  const [variants, setVariants] = useState([]);
  const [deletedVariants, setDeletedVariants] = useState([]);
  const [addons, setAddons] = useState([]);
  const [deletedAddons, setDeletedAddons] = useState([]);
  const [taxes, setTaxes] = useState({ taxUid: null, hsnCode: "" });
  const [isPublished, setIsPublished] = useState(false);
  const [itemName, setItemName] = useState("");
  const [itemCategory, setItemCategory] = useState(null);
  const [price, setPrice] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState("");
  const [pricing, setPricing] = useState([]);
  const [itemUnit, setItemUnit] = useState(1);
  const [unit, setUnit] = useState({});
  const [shortDesc, setShortDesc] = useState("");
  const [desc, setDesc] = useState("");
  const [itemType, setItemType] = useState("");
  const [isFavorite, setIsFavorite] = useState(0);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productId, setProductId] = useState(null);
  const [productUid, setProductUid] = useState(null);
  const [isItemWithVariant, setIsItemWithVariant] = useState(false);

  const [attributesKeyValue, setAttributesKeyValue] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);

  console.log("main variant:", variants);

  const handleToggle = () => {
    setIsPublished((prev) => !prev);
  };

  const loadCategoriesKeyValue = async () => {
    try {
      const categories = await dispatch(
        getCategoriesKeyValue({ storeId })
      ).unwrap();
      setCategories(categories || []);
    } catch (err) {
      handleApiError(err);
    }
  };

  const loadUnitsKeyValue = async () => {
    try {
      const units = await dispatch(getUnits({ storeId })).unwrap();
      setUnits(units || []);
    } catch (err) {
      handleApiError(err);
    }
  };

  const loadAllItemTypes = async () => {
    try {
      const types = await dispatch(getItemTypes({ storeId })).unwrap();
      setItemTypes(types || []);
    } catch (err) {
      handleApiError(err);
    }
  };

  const loadAllAttributeKeyValue = async () => {
    try {
      const attributes = await dispatch(
        getAttributesKeyValue({ storeId })
      ).unwrap();
      setAttributesKeyValue(attributes || []);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (storeId) {
      loadCategoriesKeyValue();
      loadUnitsKeyValue();
      loadAllItemTypes();
      loadAllAttributeKeyValue();
    }
  }, [storeId]);

  const loadItemDetails = async () => {
    setIsLoading(true);
    try {
      const itemDetails = await dispatch(
        getItemDetails({ storeId, itemUid: uid })
      ).unwrap();

      console.log("load itemDetails", itemDetails);

      if (
        itemDetails.variants &&
        itemDetails.variants.length > 0 &&
        itemDetails.variants[0].attributes &&
        itemDetails.variants[0].attributes.length > 0
      ) {
        setIsItemWithVariant(true);
      }
      setProductId(itemDetails.product_id || null);
      setProductUid(itemDetails.product_uid || null);

      const updatedVariants = itemDetails.variants.map((variant) => ({
        ...variant,
        selected_unit: variant.unit || "",
        original_price: variant.pricing?.[0]?.original_price || "",
        discounted_price: variant.pricing?.[0]?.final_price || "",
        unit_value: variant.unit_value || 1,
      }));
      setVariants(updatedVariants);

      const pricing = itemDetails.variants[0]?.pricing || [];
      const unit = itemDetails.variants[0]?.unit || {};
      const unitValue = itemDetails.variants[0]?.unit_value || 1;
      const isFavorite = itemDetails.is_favorite ? 1 : 0;
      const isPublished = itemDetails.is_published === 1 ? true : false;
      setItemName(itemDetails.product_name || "");
      setItemCategory(
        itemDetails.category
          ? {
              uid: itemDetails.category.product_category_uid,
              name: itemDetails.category.product_category_name,
            }
          : null
      );
      setPrice(pricing[0]?.original_price || "");
      setDiscountedPrice(pricing[0]?.final_price || "");
      setPricing(pricing || []);
      setItemUnit(unitValue);
      setUnit(unit);
      setShortDesc(itemDetails.product_short_description || "");
      setDesc(itemDetails.product_description || "");
      setItemType(itemDetails.product_type_uid || "");
      setIsFavorite(isFavorite);
      setIsPublished(isPublished);
      setAddons(itemDetails.addons || []);
      setTaxes({
        taxUid: itemDetails.tax_uid || null,
        hsnCode: itemDetails.hsn_code || "",
      });
      setIsLoading(false);
    } catch (err) {
      handleApiError(err);
      setIsLoading(false);
    }

    console.log("loaded item variants:", variants);
  };

  useEffect(() => {
    if (uid) {
      loadItemDetails();
    }
  }, [uid]);

  useEffect(() => {
    if (attributesKeyValue.length) {
      const selectedAttrsInVariants = variants[0]?.attributes || [];
      const selectedAttrs = attributesKeyValue.filter((attr) =>
        selectedAttrsInVariants.find((a) => a.attribute_uid === attr.uid)
      );
      setSelectedAttributes(selectedAttrs);
    }
  }, [variants, attributesKeyValue]);

  const goToListing = () => {
    navigate("/app/config/items");
  };

  const getVariantData = () => {
    const baseVariant = {
      ...variants[0],
      variant_name: itemName,
      description: desc,
      sku_code: "",
      in_stock: 1,
      is_new_record: 0,
      unit_value: Number(itemUnit),
      unit_uid: unit?.unit_uid || null,
      attributes: [],
      addons: [],
      images: [],
      pricing: [
        {
          ...pricing[0],
          original_price: Number(price),
          discount_value: Number(price) - Number(discountedPrice || 0),
          discount_type: "FLAT",
        },
      ],
      tax_uid: null,
    };
    if (variants.length) {
      const allVariants = [];
      variants.map((variant) => {
        const hasPricing = variant.pricing?.length > 0;
        const itemVariant = {
          ...variant,
          ...baseVariant,
          attributes: variant.attributes,
          unit_value: Number(variant?.unit_value),
          unit_uid: variant?.selected_unit?.unit_uid || null,
          sku_code: variant?.sku_code || null,
          original_price: Number(variant.original_price),
          discounted_price: Number(variant.discounted_price) || null,
          images: [],
          pricing: [
            {
              ...variant.pricing?.[0],
              original_price: Number(variant.original_price),
              discount_value:
                Number(variant.original_price) -
                Number(variant.discounted_price || 0),
              discount_type: "FLAT",
              is_new_record: hasPricing ? 0 : 1,
            },
          ],
          is_new_record: variant.is_new_record || 0,
        };
        allVariants.push(itemVariant);
      });
      return allVariants;
    } else {
      return [baseVariant];
    }
  };

  const handleSaveItem = async () => {
    try {
      const obj = {
        store_id: storeId,
        product_name: itemName,
        product_description: desc,
        product_short_description: shortDesc,
        product_category_uid: itemCategory?.uid || null,
        product_type_uid: itemType || null,
        is_favorite: isFavorite,
        is_published: isPublished ? 1 : 0,
        addons: addons,
        tax_uid: taxes.taxUid || null,
        hsn_code: taxes.hsnCode || "",
        is_kot_required: 0,
        variants: getVariantData(),
      };
      console.log("Saving Item:", obj);
      const res = await dispatch(addItem(obj)).unwrap();
      showSuccess(res.message);
      goToListing();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleUpdateItem = async () => {
    console.log("Updating Item", variants);
    try {
      const obj = {
        product_id: productId,
        product_uid: productUid,
        store_id: storeId,
        product_name: itemName,
        product_description: desc,
        product_short_description: shortDesc,
        product_category_uid: itemCategory?.uid || null,
        product_type_uid: itemType || null,
        is_favorite: isFavorite,
        is_published: isPublished ? 1 : 0,
        addons: [...addons, ...deletedAddons],
        tax_uid: taxes.taxUid || null,
        hsn_code: taxes.hsnCode || "",
        is_kot_required: 0,
        variants: [...getVariantData(), ...deletedVariants],
      };
      console.log("Saving Item:", obj);
      const res = await dispatch(updateItem(obj)).unwrap();
      showSuccess(res.message);
      goToListing();
    } catch (err) {
      handleApiError(err);
    }
  };

  return (
    <Box p={3}>
      <Container>
        <Box sx={{ maxWidth: "900px", m: "0 auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title="Back to Items">
                <IconButton onClick={() => navigate("/app/config/items")}>
                  <Icon icon="eva:arrow-back-fill" />
                </IconButton>
              </Tooltip>
              <Typography variant="h5">
                {uid ? "Edit Item" : "Add Item"}
              </Typography>
            </Stack>
          </Stack>

          <Box sx={{ maxWidth: "900px", m: "0 auto" }}>
            <Card sx={{ p: "30px", mb: 3 }}>
              <Typography
                style={{
                  color: "#000",
                  marginBottom: 24,
                  fontWeight: 600,
                }}
              >
                Item Details
              </Typography>
              {/* Item Details */}
              <Stack spacing={3} mb={3}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    value={itemName}
                    fullWidth
                    autoFocus
                    autoComplete="item_name"
                    name="item_name"
                    label="Item Name *"
                    onChange={(event) => setItemName(event.target.value)}
                  />

                  <Autocomplete
                    fullWidth
                    value={itemCategory}
                    options={categories}
                    getOptionLabel={(option) => option?.name || ""}
                    renderInput={(params) => (
                      <TextField
                        fullWidth
                        {...params}
                        label="Item Category *"
                      />
                    )}
                    onChange={(event, newValue) => setItemCategory(newValue)}
                  />
                </Stack>

                <Stack direction="row" spacing={2}>
                  <TextField
                    autoComplete="item_price"
                    name="item_price"
                    label="Price *"
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    helperText="Regular selling price of the item"
                  />

                  <TextField
                    autoComplete="item_discounted_price"
                    name="item_discounted_price"
                    label="Sale Price (Optional)"
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={discountedPrice}
                    onChange={(e) => setDiscountedPrice(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    helperText="Visible discount price (must be less than price)"
                  />
                </Stack>

                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ width: "100%" }}>
                    <TextField
                      fullWidth
                      autoComplete="off"
                      type="number"
                      name="item_unit"
                      label="Item Unit *"
                      value={itemUnit}
                      onChange={(event) => {
                        setItemUnit(event.target.value);
                      }}
                      helperText="Enter quantity for unit (e.g., 1, 500, etc.)"
                    />
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 15,
                      width: "100%",
                    }}
                  >
                    <Box
                      style={{
                        minWidth: 200,
                      }}
                    >
                      <Autocomplete
                        value={unit}
                        options={units}
                        getOptionLabel={(option) =>
                          option.unit_name ? option.unit_name : ""
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Unit *"
                            helperText="Choose unit type (e.g., Liter, Kg, Piece)"
                          />
                        )}
                        onChange={(event, newInputValue) => {
                          setUnit(newInputValue);
                        }}
                      />
                    </Box>
                    <Typography
                      style={{
                        fontSize: 14,
                        marginTop: "-18px",
                      }}
                    >
                      {itemUnit &&
                        unit?.unit_name &&
                        `per ${itemUnit} ${unit.unit_name}`}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {/* Item Type Dropdown */}
                  <FormControl fullWidth>
                    <InputLabel id="item-type-label">
                      Item Type (Optional)
                    </InputLabel>
                    <Select
                      labelId="item-type-label"
                      id="item-type-select"
                      value={itemType}
                      label="  Item Type (Optional)"
                      onChange={(e) => setItemType(e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {itemTypes?.map((item) => (
                        <MenuItem
                          key={item.product_type_uid}
                          value={item.product_type_uid}
                        >
                          {item.product_type_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Favorite Dropdown */}
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="favorite-label">
                      Add to Favorite (Optional)
                    </InputLabel>
                    <Select
                      labelId="favorite-label"
                      id="favorite-select"
                      value={isFavorite}
                      label=" Add to Favorite (Optional)"
                      onChange={(e) => setIsFavorite(e.target.value)}
                    >
                      <MenuItem value={0}>No</MenuItem>
                      <MenuItem value={1}>Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <TextField
                  helperText="Short description about item (Maximum 60 characters in length)"
                  value={shortDesc}
                  autoComplete="off"
                  name="item_short_desc"
                  label="Short Description (Optional)"
                  multiline
                  rows={2}
                  onChange={(event) => setShortDesc(event.target.value)}
                />

                <TextField
                  value={desc}
                  autoComplete="off"
                  name="item_long_desc"
                  label="Description (Optional)"
                  multiline
                  rows={2}
                  onChange={(event) => setDesc(event.target.value)}
                />
              </Stack>

              {/* Item Images */}
              <Box>
                <Box sx={{ mb: 4 }}>
                  <input
                    //   onChange={(event) => handleFileChange(event)}
                    accept="image/*"
                    type="file"
                    //   ref={inputFile}
                    style={{ display: "none" }}
                  />

                  <Typography
                    style={{
                      color: "#000",
                      marginBottom: 24,
                      fontWeight: 600,
                    }}
                  >
                    Item Images
                  </Typography>
                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          width: 155,
                          height: 155,
                          m: "auto",
                          display: "flex",
                          cursor: "pointer",
                          overflow: "hidden",
                          borderRadius: "15%",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                          border: "1px dashed rgba(145, 158, 171, 0.32)",
                        }}
                      >
                        {!false && (
                          <Link
                            //   onClick={handleFileClick}
                            style={{ textDecoration: "none" }}
                          >
                            <Box
                              sx={{
                                width: 136,
                                height: 136,
                                textAlign: "center",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                color: "rgb(145, 158, 171)",
                                bgcolor: "rgb(244, 246, 248)",
                                transition:
                                  "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                                borderRadius: "15%",
                              }}
                            >
                              <Iconify
                                sx={{ width: 40 }}
                                icon="mdi:camera-plus"
                              />
                              <Typography sx={{ fontSize: 14 }}>
                                Upload image
                              </Typography>
                            </Box>
                          </Link>
                        )}
                      </Box>

                      <Typography
                        sx={{
                          mt: 2,
                          lineHeight: 1.5,
                          fontSize: "0.75rem",
                          fontWeight: 400,
                          color: "rgb(99, 115, 129)",
                          textAlign: "center",
                          display: "block",
                        }}
                      >
                        Allowed *.jpeg, *.jpg, *.png max size of 1 MB
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <ItemImagesView />
              </Box>
            </Card>

            <Card sx={{ p: "30px", mb: 3 }}>
              <Typography
                style={{
                  color: "#000",
                  marginBottom: 24,
                  fontWeight: 600,
                }}
              >
                Additional Details
              </Typography>
              <Box>
                <ItemAdditionalInfo
                  variants={variants}
                  setVariants={setVariants}
                  deletedVariants={deletedVariants}
                  setDeletedVariants={setDeletedVariants}
                  attributesKeyValue={attributesKeyValue}
                  selectedAttributes={selectedAttributes}
                  setSelectedAttributes={setSelectedAttributes}
                  isItemWithVariant={isItemWithVariant}
                  addons={addons}
                  setAddons={setAddons}
                  deletedAddons={deletedAddons}
                  setDeletedAddons={setDeletedAddons}
                  taxes={taxes}
                  setTaxes={setTaxes}
                />
              </Box>
            </Card>

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 4,
                mb: 4,
              }}
            >
              {/* Left side: Draft / Publish Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={isPublished}
                    onChange={handleToggle}
                    color="primary"
                  />
                }
                label={isPublished ? "Published" : "Draft"}
              />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => navigate("/app/config/items")}>
                  Cancel
                </Button>
                <AppButton
                  disabled={isLoading}
                  isLoading={isLoading}
                  onClick={uid ? handleUpdateItem : handleSaveItem}
                  variant="contained"
                >
                  {uid ? "Update Item" : "Save Item"}
                </AppButton>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
